import { useSignalEffect } from '@preact/signals-react';
import { useEffect, useRef } from 'react';
import Head from 'next/head.js';
import Link from 'next/link.js';
import { useSearchParams } from 'next/navigation.js';
import { Heading } from '~/ui/Typography/Heading.jsx';
import {
	BookmarkIcon,
	BookmarkIconSymbol,
	ChevronIcon,
	ChevronIconSymbol,
	SearchIcon,
	SearchIconSymbol,
	CloseIcon,
	CloseIconSymbol,
} from '~/ui/Images/mod.js';
import { useBooleanState, useEffectOnMounted } from '~/lib/hooks/mod.js';
import { tkns } from '~/lib/str/mod.js';
import { compose } from '~/lib/fn/compose.js';
import { useDisplayYear, withAppState } from '~/features/AppState/mod.js';
import { dec, inc } from '~/lib/num/mod.js';
import { apply } from '~/lib/fn/mod.js';
import { isNumber } from '~/lib/lang/mod.type.js';
import { useGlobalSearch } from '~/features/Schedule/mod.js';
import { PersistentStorage } from '~/lib/data/mod.storage.js';

import { scheduleSearchIndex } from '~/apps.deps.js';

import { getClampedFontSizePx, isLogoPulseDue } from './utils.js';

/**
 * Key of the `localStorage` entry (via {@link PersistentStorage}) holding a JSON object of
 * UI-related settings remembered from user behaviors (as opposed to `event:${year}`, which holds
 * persisted app/event state).
 */
const ADAPTIVE_UI_STORAGE_KEY = 'adaptiveUi';

export const Layout = ({
	// OWN
	pageTitle,
	footer = null,
	className,
	children,
	meta = {},
	resources = [],
	favicons = [],
	// AppState
	loaded: appStateLoaded,
	hasRuntimeJs,
	year: appStateYear,
	layout,
}) => {
	const { fontSize } = layout;
	const [menuIsOpen, { toggle: toggleMenu }] = useBooleanState(false);
	const menuDrawerEl = useRef();
	const searchFieldEl = useRef();

	// Draw attention to the menu button's logo with a pulse animation, unless the menu has been
	// opened recently enough that the user already knows it's there. See `./utils.js`.
	//
	// Once the menu is opened, `is-finishing` fades the pulse out -- while it keeps animating
	// underneath -- and stays on for the rest of the session so closing the menu doesn't bring the
	// pulse back. `is-pulsing` is only removed after that fade-out finishes (see `onTransitionEnd`
	// below), so the animation never gets cut off while it's still visible.
	const [logoPulseIsActive, { set: setLogoPulseIsActive, off: stopLogoPulse }] =
		useBooleanState(false);
	const [logoPulseIsFinishing, { on: finishLogoPulse }] = useBooleanState(false);

	useEffectOnMounted(() => {
		const { menuLastOpenedAt } = PersistentStorage.get(ADAPTIVE_UI_STORAGE_KEY) ?? {};

		setLogoPulseIsActive(isLogoPulseDue(menuLastOpenedAt));
	});

	useEffect(() => {
		if (!menuIsOpen) {
			return;
		}

		finishLogoPulse();
		PersistentStorage.set(ADAPTIVE_UI_STORAGE_KEY, {
			...PersistentStorage.get(ADAPTIVE_UI_STORAGE_KEY),
			menuLastOpenedAt: Date.now(),
		});
	}, [menuIsOpen, finishLogoPulse]);

	const [searchIsActive, searchApi] = useGlobalSearch(searchText => searchText.trim() !== '');

	const urlParams = useSearchParams();
	const filterIsBookmarks = urlParams.get('filter') === 'bookmarks';
	const year = useDisplayYear(appStateYear, meta['event-year']);
	const rootClassName = tkns(className, {
		js: hasRuntimeJs.value,
		'filter-bookmarks': filterIsBookmarks,
		'filter-search': searchIsActive.value,
	});

	useEffectOnMounted(() => {
		scheduleSearchIndex.init();
	});

	useEffect(() => {
		if (!year) {
			return;
		}

		const yearClassName = `year-${year}`;

		document.documentElement.classList.add(yearClassName);
		return () => document.documentElement.classList.remove(yearClassName);
	}, [year]);

	useEffect(() => {
		if (appStateLoaded.value && fontSize.value == null) {
			const rawFontSize = window
					.getComputedStyle(document.documentElement)
					.getPropertyValue('font-size'),
				parsedFontSize = Number(rawFontSize.replace(/\D/g, '')),
				clampedFontSize = getClampedFontSizePx(parsedFontSize);

			if (!Number.isNaN(parsedFontSize)) {
				fontSize.value = clampedFontSize;
			}
		}
	}, [appStateLoaded.value, fontSize]);

	useSignalEffect(() => {
		if (isNumber(fontSize.value)) {
			document
				.querySelector(':root')
				.style.setProperty('--dynamic-font-size', `${fontSize.value}px`);
		}
	});

	return (
		<div id="app" className={rootClassName}>
			<Head>
				<title>{`Clockwork Alchemy ${year} ${pageTitle} Schedule App`}</title>
				{favicons.map(item => (
					<link key={item.href} {...item} href={item.href.replaceAll('{year}', year)} />
				))}
				{/* <link rel="icon" href="/CAlogo.svg" type="image/svg+xml" /> */}
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				{Object.keys(meta).map(name => (
					<meta key={name} name={name} content={meta[name]} />
				))}
			</Head>
			<svg width="0" height="0">
				<BookmarkIconSymbol />
				<ChevronIconSymbol />
				<CloseIconSymbol />
				<SearchIconSymbol />
			</svg>
			<header>
				<div className="track">
					<div className="track-item">
						<Heading headingLevel={1} className="main-title">
							<a href="https://www.clockworkalchemy.com/" target="mainsite">
								<img
									src={`/${year}/ca-theme-logotext.png`}
									alt={pageTitle}
									className="main-title-image"
								/>
							</a>
						</Heading>
					</div>

					<div className="track-item menu">
						<button
							type="button"
							className="btn btn-graphic menu-button"
							disabled={!hasRuntimeJs.value}
							aria-label={hasRuntimeJs.value ? `${menuIsOpen ? 'Hide' : 'Show'} Menu` : undefined}
							aria-expanded={String(menuIsOpen)}
							aria-controls="menu-drawer"
							onClick={() => {
								toggleMenu();
							}}
						>
							<SearchIcon
								size="16"
								className={tkns('menu-filter-icon', { 'is-active': searchIsActive.value })}
								hidden={!searchIsActive.value}
							/>
							<BookmarkIcon
								size="16"
								className={tkns('menu-filter-icon', { 'is-active': filterIsBookmarks })}
								hidden={!filterIsBookmarks}
							/>

							<ChevronIcon size="16" className="menu-button-icon" />
							<span className="logo-wrap">
								<span
									className={tkns('logo-pulse', {
										'is-pulsing': logoPulseIsActive,
										'is-finishing': logoPulseIsFinishing,
									})}
									aria-hidden="true"
									onTransitionEnd={ev => {
										// Only once the fade-out has finished is it safe to stop the pulse without
										// visibly cutting it off.
										if (ev.propertyName === 'opacity' && logoPulseIsFinishing) {
											stopLogoPulse();
										}
									}}
								/>
								<img src={`/${year}/ca-logomark.png`} alt="" className="logo" />
							</span>
						</button>
					</div>
				</div>
				<div
					className="menu-drawer stack"
					id="menu-drawer"
					ref={menuDrawerEl}
					aria-hidden={String(!menuIsOpen)}
					tabIndex={menuIsOpen ? 0 : -1}
				>
					<search className="search">
						<form
							onSubmit={ev => {
								const formData = new FormData(ev.target);

								searchApi.set(formData.get('search').trim());
								ev.preventDefault();
							}}
						>
							<input
								type="search"
								name="search"
								ref={searchFieldEl}
								aria-labelledby="search-submit"
							/>
							<button
								type="button"
								onClick={() => ((searchFieldEl.current.value = ''), searchApi.clear())}
								aria-label="Clear Search"
							>
								<CloseIcon size="12" />
							</button>
							<button type="submit" id="search-submit">
								Search
								<SearchIcon size="16" />
							</button>
						</form>
					</search>
					<section className="controls a11y">
						<h2 className="controls-title a11y-title">Text Size:</h2>
						<menu className="controls-list filters-list">
							<li>
								{apply(fontSize <= getClampedFontSizePx.Min, isDisabled => (
									<button
										type="button"
										className="action action-text controls-item text-smaller"
										aria-label="Decrease text size"
										aria-disabled={isDisabled ? 'true' : undefined}
										onClick={() => {
											if (!isDisabled) {
												fontSize.value = getClampedFontSizePx(dec(fontSize.value));
											}
										}}
									>
										Smaller
									</button>
								))}
							</li>
							<li>
								{apply(fontSize >= getClampedFontSizePx.Max, isDisabled => (
									<button
										type="button"
										className="action action-text controls-item"
										aria-label="Increase text size"
										aria-disabled={isDisabled ? 'true' : undefined}
										onClick={() => {
											if (!isDisabled) {
												fontSize.value = getClampedFontSizePx(inc(fontSize.value));
											}
										}}
									>
										Bigger
									</button>
								))}
							</li>
						</menu>
					</section>
					<section className="controls filters">
						<h2 className="drawer-title controls-title filters-title">Show:</h2>
						<menu className="controls-list filters-list">
							<li>
								{/* NOTE href="" means to link to the current path with no query params or hashes */}
								<Link
									href=""
									className="action action-text controls-item filters-item"
									aria-current={!urlParams.has('filter') ? 'true' : undefined}
								>
									All
								</Link>
							</li>
							<li>
								<Link
									href="?filter=bookmarks"
									className="action action-text controls-item filters-item"
									aria-current={urlParams.get('filter') === 'bookmarks' ? 'true' : undefined}
								>
									Bookmarked
									<BookmarkIcon size="16" className="controls-action-icon" />
								</Link>
							</li>
						</menu>
					</section>
					{/* New feature starting in 2025. No Resources for earlier years. */}
					{year >= 2025 ?
						<section className="controls links">
							<h2 className="controls-title">Resources:</h2>
							<ul className="links-list">
								{resources.map(({ label, url }) => (
									<li key={label}>
										<a href={url} target="_blank">
											{label}
										</a>
									</li>
								))}
							</ul>
						</section>
					:	null}
				</div>
			</header>
			<main>
				<a id="top" hidden></a>
				{children}
			</main>
			<footer>{footer}</footer>
		</div>
	);
};

export default compose(withAppState)(Layout);
